import { ref, onMounted, onUnmounted, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import { getNotifications } from '@/services/notificationsService';
import { useNotificationsStore, mapNotificationFromApi } from '@/stores/notificationsStore';

const POLL_INTERVAL_MS = 60000; // 1 minute
const TOAST_LIFE_MS = 6000;

/**
 * Composable to show real-time notification toasts by polling GET /api/v1/notifications.
 * Call from the app shell (e.g. App.vue) and pass a ref/computed that is true when the user is authenticated.
 * Polls only when the tab is visible; stops when tab is hidden or on unmount.
 *
 * Reminder notifications use a persistent toast (life: 0) and must be manually dismissed.
 *
 * For true real-time (no polling), the backend would push over WebSocket/SSE – see docs/notifications-realtime-toast.md.
 */
export function useNotificationToasts(isAuthenticatedRef) {
    const toast = useToast();
    const store = useNotificationsStore();
    const seenIds = ref(new Set());
    const initialLoadDone = ref(false);
    let intervalId = null;

    function severityForNotification(mapped) {
        if (mapped.type === 'reminder') return 'warn';
        if (mapped.type === 'freelancer_shift_cancellation') return 'error';
        if (mapped.type === 'freelancer_shift_acceptance' && mapped.variant === 'denied') return 'error';
        if (mapped.type === 'freelancer_shift_acceptance' && mapped.variant === 'mixed') return 'warn';
        if (mapped.type === 'freelancer_shift_acceptance') return 'success';
        return 'info';
    }

    function showToastForNotification(mapped, raw) {
        const severity = severityForNotification(mapped);
        const isReminder = mapped.type === 'reminder';
        const opts = {
            severity,
            summary: mapped.title || 'Notification',
            detail: mapped.subtitle || '',
            group: isReminder ? 'reminder' : undefined,
            data: isReminder
                ? {
                    type: 'reminder',
                    notificationId: raw.id,
                    link: mapped.link,
                    reminderId: raw.data?.reminder_id,
                    projectId: raw.data?.project_id ?? mapped.projectId
                }
                : undefined
        };
        if (!isReminder) opts.life = TOAST_LIFE_MS;
        toast.add(opts);
    }

    const debug = () => typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_REMINDER_TOASTS') === '1';

    async function poll() {
        if (!isAuthenticatedRef?.value) return;
        try {
            const { list: rawList } = await getNotifications({ page: 1, per_page: 20 });
            const isFirstLoad = !initialLoadDone.value;
            if (debug()) {
                const reminders = (rawList || []).filter((r) => String(r.type || '').toLowerCase() === 'reminder');
                console.log('[ReminderToast] Poll ran, got', (rawList || []).length, 'notifications', reminders.length, 'reminders. First load:', isFirstLoad);
                if (reminders.length > 0) {
                    console.log('[ReminderToast] Sample reminder:', reminders[0]);
                }
            }
            let firstLoadReminderToastCount = 0;
            const MAX_FIRST_LOAD_REMINDER_TOASTS = 5;

            for (const raw of rawList || []) {
                const id = String(raw.id);
                if (seenIds.value.has(id)) continue;
                seenIds.value.add(id);
                const mapped = mapNotificationFromApi(raw);
                if (!mapped) continue;
                store.prependNotification(mapped);
                const isUnread = !raw.read_at;
                const isReminder = mapped.type === 'reminder';

                if (isUnread && isReminder) {
                    const showOnFirstLoad = isFirstLoad && firstLoadReminderToastCount < MAX_FIRST_LOAD_REMINDER_TOASTS;
                    const showOnSubsequentPoll = !isFirstLoad;
                    if (showOnFirstLoad || showOnSubsequentPoll) {
                        if (showOnFirstLoad) firstLoadReminderToastCount++;
                        if (debug()) {
                            console.log('[ReminderToast] Showing reminder toast id=', raw.id, 'firstLoad=', isFirstLoad);
                        }
                        showToastForNotification(mapped, raw);
                    }
                } else if (!isFirstLoad && isUnread && !isReminder) {
                    showToastForNotification(mapped, raw);
                }
            }
            if (isFirstLoad) initialLoadDone.value = true;
            // Prune seenIds to avoid unbounded growth (keep last 100)
            if (seenIds.value.size > 100) {
                const ids = [...seenIds.value];
                seenIds.value = new Set(ids.slice(-80));
            }
        } catch (err) {
            console.error('[useNotificationToasts] Poll failed:', err);
        }
    }

    function startPolling() {
        if (intervalId) return;
        poll(); // run once immediately
        intervalId = setInterval(poll, POLL_INTERVAL_MS);
    }

    function stopPolling() {
        if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
        }
    }

    function onVisibilityChange() {
        if (document.visibilityState === 'visible' && isAuthenticatedRef?.value) {
            startPolling();
        } else {
            stopPolling();
        }
    }

    onMounted(() => {
        document.addEventListener('visibilitychange', onVisibilityChange);
        if (isAuthenticatedRef?.value) {
            store.list.forEach((n) => seenIds.value.add(String(n.id)));
            if (document.visibilityState === 'visible') startPolling();
        }
    });

    watch(
        () => isAuthenticatedRef?.value,
        (authenticated) => {
            if (authenticated) {
                store.list.forEach((n) => seenIds.value.add(String(n.id)));
                if (document.visibilityState === 'visible') startPolling();
            } else {
                stopPolling();
                initialLoadDone.value = false;
            }
        },
        { immediate: false }
    );

    onUnmounted(() => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        stopPolling();
    });

    return { poll, startPolling, stopPolling };
}
